import { Frame, Label, Node, Pill, fx } from './frame';
import { useStrings } from '../../../state/useLocale';
import type { FigureProps } from './index';

/**
 * The two figures about finding something you cannot see.
 *
 * `reflog` is the one this product is named after, so its job is to make the
 * word "unreachable" mean something physical: the commit is still drawn, it is
 * just drawn the way the live graph draws work nothing points at any more, and
 * a line in the reflog still holds its hash. `bisect` is the opposite problem
 * — the commit exists and you cannot say which one it is — and its job is to
 * make the halving visible as a length.
 *
 * The reflog rows are printed output and stay whole, hash, selector and verb.
 * `good` and `bad` stay too: they are `git bisect good` and `git bisect bad`,
 * which is what the learner types at this prompt. See frame.tsx, `Label`.
 */

/* ── reflog ──────────────────────────────────────────────────────────────────
   The lost commit is drawn exactly as GitGraph.tsx draws an unreachable one:
   the panel's own ground inside a dashed ring at three-quarter strength, on a
   dashed edge, with the dashed italic `unreachable` pill beside it. The green
   is the reflog line that still names it, and the leader that connects the two
   is the whole recovery: nothing points at the commit, and the reflog is not
   nothing. */

export function ReflogFigure({ active }: FigureProps) {
  const f = useStrings().warmup.fig;
  const rows = [
    { hash: '9f8e7d6', sel: 'HEAD@{0}', what: 'reset', y: 44, lost: false },
    { hash: 'a1b2c3d', sel: 'HEAD@{1}', what: 'commit', y: 66, lost: true },
    { hash: '4c5d6e7', sel: 'HEAD@{2}', what: 'commit', y: 88, lost: false },
  ];

  return (
    <Frame name="reflog" active={active}>
      <g className="fx-in" style={fx(0)}>
        <path className="edge" d="M26 40 V96" />
        <Node x={26} y={40} r={5} />
        <Node x={26} y={96} r={5} />
      </g>

      <Pill className="fx-in" style={fx(150)} x={40} y={32} text="main" kind="head" />

      {rows.map((row, i) => (
        <text key={row.sel} className="fx-rise" style={fx(200 + i * 80)} x={120} y={row.y}>
          <tspan className={row.lost ? 'lbl-sig' : 'lbl-dim'}>{row.hash}</tspan>
          <tspan className={row.lost ? 'lbl-ink' : undefined}>{` ${row.sel}`}</tspan>
          <tspan className="lbl-dim">{`: ${row.what}`}</tspan>
        </text>
      ))}

      {/* The commit the reset walked away from. */}
      <g className="fx-in" style={fx(520)}>
        <path className="edge ghost" d="M52 68 C52 82, 26 82, 26 96" />
        <Node x={52} y={68} r={5} kind="ghost" />
      </g>

      {/* The same pill the live graph hangs off an unreachable commit, and
          the same words (play.ts, `graph.unreachable`). `Pill` sizes itself
          from the label it is given, so the Arabic one is a shorter pill
          rather than a label out of the side of it. */}
      <g className="fx-in" style={fx(700)}>
        <path className="wire ghost" d="M50 106 V76" />
        <Pill x={14} y={106} text={f.unreachable} kind="ghost" />
      </g>

      <path className="wire sig fx-draw" style={fx(960, { '--t': '280ms', '--len': 60 })} d="M114 66 H60" />

      <Label className="lbl-dim fx-in" style={fx(1100)} x={110} y={134} at="left">
        {f.reflogHasHash}
      </Label>
    </Frame>
  );
}

/* ── bisect ──────────────────────────────────────────────────────────────────
   A range with a known-good end and a known-bad end, and the one commit worth
   testing next. The two brackets are the argument: the second is half the
   first, drawn at half its length, so "halves the range" is measured on the
   page rather than asserted. The midpoint is the only green thing, because it
   is the only commit the learner has to do anything about. */

export function BisectFigure({ active }: FigureProps) {
  const f = useStrings().warmup.fig;
  const commits = [24, 58, 92, 126, 160, 194, 228, 262, 296];

  return (
    <Frame name="bisect" active={active}>
      <g className="fx-in" style={fx(0)}>
        <path className="edge" d="M24 46 H296" />
        {commits.map((x) => (
          <Node key={x} x={x} y={46} r={5} kind="dim" />
        ))}
      </g>

      <g className="fx-in" style={fx(250)}>
        <text x={24} y={68} textAnchor="middle">
          good
        </text>
        <text x={296} y={68} textAnchor="middle">
          bad
        </text>
      </g>

      <path className="wire fx-draw" style={fx(340, { '--t': '280ms', '--len': 300 })} d="M24 84 V90 H296 V84" />

      <Node className="fx-pop" style={fx(680)} x={160} y={46} r={5} kind="signal" />
      <Label className="fx-in" style={fx(740)} x={160} y={30}>
        {f.midpoint}
      </Label>

      <path
        className="wire bright fx-draw"
        style={fx(840, { '--t': '240ms', '--len': 160 })}
        d="M24 100 V106 H160 V100"
      />

      <Label className="lbl-dim fx-in" style={fx(1060)} x={160} y={128}>
        {f.halves}
      </Label>
    </Frame>
  );
}
